import { useT } from '../i18n/messages';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { ActionIconButton } from './DataViewActionCell';

export type DialogSize = 'small' | 'medium' | 'large' | 'fullscreen';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

let openDialogCount = 0;
let dialogLayer = 0;

function lockBodyScroll() {
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  document.body.dataset.dialogOpen = 'true';
  document.body.style.overflow = 'hidden';
  if (scrollbarWidth > 0) {
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  }
}

function unlockBodyScroll() {
  document.body.removeAttribute('data-dialog-open');
  document.body.style.overflow = '';
  document.body.style.paddingRight = '';
}

export function Dialog({
  children,
  closeOnBackdrop = true,
  onClose,
  open,
  size = 'medium',
  title,
}: {
  children: ReactNode;
  closeOnBackdrop?: boolean;
  onClose: () => void;
  open: boolean;
  size?: DialogSize;
  title: ReactNode;
}) {
  const t = useT();
  const titleId = useId();
  const bodyId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [layer, setLayer] = useState(0);

  useEffect(() => {
    if (!open) {
      return;
    }

    const previousFocus = document.activeElement as HTMLElement | null;
    openDialogCount += 1;
    dialogLayer += 1;
    setLayer(dialogLayer);
    if (openDialogCount === 1) {
      lockBodyScroll();
    }

    const frame = window.requestAnimationFrame(() => {
      const focusTarget =
        panelRef.current?.querySelector<HTMLElement>('[autofocus]') ??
        panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ??
        panelRef.current;
      focusTarget?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
      previousFocus?.focus();
      openDialogCount = Math.max(0, openDialogCount - 1);
      if (openDialogCount === 0) {
        unlockBodyScroll();
        dialogLayer = 0;
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, open]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  function handleBackdropMouseDown(event: MouseEvent<HTMLDivElement>) {
    if (closeOnBackdrop && event.target === event.currentTarget) {
      onClose();
    }
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Tab') {
      return;
    }

    const focusable = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? [],
    ).filter((element) => !element.hasAttribute('disabled'));

    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;

    if (event.shiftKey) {
      if (!active || active === first || !panelRef.current?.contains(active)) {
        event.preventDefault();
        last.focus();
      }
      return;
    }

    if (!active || active === last || !panelRef.current?.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  }

  return createPortal(
    <div
      className="dialog-backdrop"
      onMouseDown={handleBackdropMouseDown}
      style={{ zIndex: 1000 + layer * 10 }}
    >
      <div
        aria-describedby={bodyId}
        aria-labelledby={titleId}
        className={`dialog-panel dialog-panel--${size}`}
        onKeyDown={handleKeyDown}
        ref={panelRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="dialog-header">
          <h2 id={titleId}>{title}</h2>
          <ActionIconButton icon="close" label={t('billing.ui.close.dialog')} onClick={onClose} />
        </div>
        <div className="dialog-body" id={bodyId}>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
